import React, { createContext, useContext, useState, useEffect } from 'react';
import { Car, CarContextType, CarFilters } from '../types';
import { simpleFirestoreService } from '../utils/simpleFirestoreService';

const CarContext = createContext<CarContextType | undefined>(undefined);

export const useCars = () => {
  const context = useContext(CarContext);
  if (context === undefined) {
    throw new Error('useCars must be used within a CarProvider');
  }
  return context;
};

export const CarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const loadCars = async () => {
    try {
      setLoading(true);
      console.log('📡 Loading cars from Firestore...');
      const result = await simpleFirestoreService.getAllCars();
      
      console.log('📥 Load result:', result);
      
      if (result.success && result.data) {
        const firestoreCars = result.data as Car[];
        console.log('🚗 Loaded cars:', firestoreCars.length, 'cars');
        console.log('🔍 First car sample:', firestoreCars[0]);
        setCars(firestoreCars);
      } else {
        console.error('❌ Error loading cars from Firestore:', result.error);
        setCars([]); // Empty array instead of INITIAL_CARS
      }
    } catch (error) {
      console.error('💥 Error loading cars from Firestore:', error);
      setCars([]); // Empty array instead of INITIAL_CARS
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCars();
  }, []);

  const addCar = async (carData: Omit<Car, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newCar: Car = {
        ...carData,
        id: Date.now().toString(),
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      const result = await simpleFirestoreService.addCar(newCar);
      
      if (result.success) {
        setCars(prev => [...prev, newCar]);
      } else {
        console.error('Error adding car to Firestore:', result.error);
      }
    } catch (error) {
      console.error('Error adding car:', error);
    }
  };

  const updateCar = async (id: string, updates: Partial<Car>) => {
    try {
      console.log('🔄 Updating car:', id, 'with updates:', updates);
      const updatedCarData = { ...updates, updatedAt: new Date() };
      const result = await simpleFirestoreService.updateCar(id, updatedCarData);
      
      console.log('📤 Update result:', result);
      
      if (result.success) {
        console.log('✅ Update successful, reloading cars...');
        // Reload all cars from Firestore to ensure data consistency
        await loadCars();
        console.log('🔄 Cars reloaded after update');
      } else {
        console.error('❌ Error updating car in Firestore:', result.error);
      }
    } catch (error) {
      console.error('💥 Error updating car:', error);
    }
  };

  const deleteCar = async (id: string) => {
    try {
      const result = await simpleFirestoreService.deleteCar(id);
      
      if (result.success) {
        setCars(prev => prev.filter(car => car.id !== id));
      } else {
        console.error('Error deleting car from Firestore:', result.error);
      }
    } catch (error) {
      console.error('Error deleting car:', error);
    }
  };

  const getCarById = (id: string): Car | undefined => {
    return cars.find(car => car.id === id);
  };

  const searchCars = (query: string): Car[] => {
    if (!query.trim()) return cars;
    
    const lowercaseQuery = query.toLowerCase();
    return cars.filter(car =>
      car.make.toLowerCase().includes(lowercaseQuery) ||
      car.model.toLowerCase().includes(lowercaseQuery) ||
      car.description.toLowerCase().includes(lowercaseQuery) ||
      car.color.toLowerCase().includes(lowercaseQuery)
    );
  };

  const filterCars = (filters: CarFilters): Car[] => {
    return cars.filter(car => {
      if (filters.make && car.make !== filters.make) return false;
      if (filters.model && car.model !== filters.model) return false;
      if (filters.fuelType && car.fuelType !== filters.fuelType) return false;
      if (filters.transmission && car.transmission !== filters.transmission) return false;
      if (filters.condition && car.condition !== filters.condition) return false;
      if (filters.color && car.color !== filters.color) return false;
      
      if (filters.yearRange) {
        const [minYear, maxYear] = filters.yearRange;
        if (car.year < minYear || car.year > maxYear) return false;
      }
      
      if (filters.priceRange) {
        const [minPrice, maxPrice] = filters.priceRange;
        if (car.price < minPrice || car.price > maxPrice) return false;
      }
      
      if (filters.mileageRange) {
        const [minMileage, maxMileage] = filters.mileageRange;
        if (car.mileage < minMileage || car.mileage > maxMileage) return false;
      }
      
      return true;
    });
  };

  // Get available and pending cars for public view (exclude sold cars)
  const getAvailableCars = (): Car[] => {
    return cars.filter(car => car.saleStatus === 'available' || car.saleStatus === 'pending');
  };

  // Get all cars for admin view
  const getAllCars = (): Car[] => {
    return cars;
  };

  const value: CarContextType = {
    cars,
    addCar,
    updateCar,
    deleteCar,
    getCarById,
    searchCars,
    filterCars,
    getAvailableCars,
    getAllCars,
    loading,
  };

  return (
    <CarContext.Provider value={value}>
      {children}
    </CarContext.Provider>
  );
};